import { createSelector } from '@reduxjs/toolkit'

import Constants from 'Constants'
// eslint-disable-next-line import/no-cycle -- legacy cycle via providers/store; out of scope to refactor here
import { getUserInfo } from 'data/userInfo/userInfoSelectors'
import { ALL_PERMISSION_KEYS } from 'settings/constants/permission'
import { get } from 'utils/lodash'
import { hasReadPermissions, hasAddPermissions, hasCreatePermissions, hasDeletePermissions, hasUpdatePermissions } from 'utils/permissions'

type UserInfo = Record<string, unknown>

const { CAN_SEE_REPORTS } = Constants
const {
  INVOICES_PERMISSION_KEY,
  PAYMENTS_PERMISSION_KEY,
  CUSTOMER_MENU_PERMISSION_KEY,
  SERVICE_PERMISSION_KEY,
  INVOICED_WORK_ORDER_DATE_PERMISSION_KEY,
  INSPECTIONS_PERMISSION_KEY,
  CONFIGURED_SERVICE_PRICE_PERMISSION_KEY,
  CUSTOMER_BILLING_PROFILE_PERMISSION_KEY,
  PRICING_MENU_PERMISSION_KEY,
  SETTING_MENU_PERMISSION_KEY,
  NOTIFICATION_PERMISSION_KEY,
  USER_SETTINGS_PERMISSION_KEY,
  PERMISSIONS_SETTINGS_PERMISSION_KEY,
  GLOBAL_SEARCH_PERMISSION_KEY,
  DASHBOARD_MENU_PERMISSION_KEY,
  ROUTE_MENU_PERMISSION_KEY,
  BILLING_MENU_PERMISSION_KEY,
  REPORT_MENU_PERMISSION_KEY,
  POST_INVOICES_PAYMENT_PERMISSION_KEY,
  RESET_PASSWORD_PERMISSION_KEY,
  LOCK_DATE_PERMISSION_KEY,
  EXCEPTIONS_MENU_PERMISSION_KEY,
  GEO_ZONE_PERMISSION_KEY,
  MESSAGING_PERMISSION_KEY,
} = ALL_PERMISSION_KEYS
const DEFAULT_PERMISSION = 0

const getAuthReducer = (state: unknown = {}): UserInfo => get(state, 'AuthReducer.userInfo', {}) as UserInfo

const selectUserInfo = createSelector(getAuthReducer, (userInfo: UserInfo = {}): UserInfo => userInfo)

export const canSeeCustomerBillingProfiles = createSelector([selectUserInfo], userInfo => {
  return hasReadPermissions(get(userInfo, CUSTOMER_BILLING_PROFILE_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canSeeService = createSelector([selectUserInfo], userInfo => {
  return hasReadPermissions(get(userInfo, SERVICE_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canSeeGlobalSearch = createSelector([selectUserInfo], userInfo => {
  return hasReadPermissions(get(userInfo, GLOBAL_SEARCH_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canSeeUserSettings = createSelector([selectUserInfo], userInfo => {
  return hasReadPermissions(get(userInfo, USER_SETTINGS_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canSeePermissionSettings = createSelector([selectUserInfo], userInfo => {
  return hasReadPermissions(get(userInfo, PERMISSIONS_SETTINGS_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canSeeInvoices = createSelector([selectUserInfo], userInfo => {
  return hasReadPermissions(get(userInfo, INVOICES_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canSeePayments = createSelector([selectUserInfo], userInfo => {
  return hasReadPermissions(get(userInfo, PAYMENTS_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canSeeLockDate = createSelector([selectUserInfo], userInfo => {
  return hasReadPermissions(get(userInfo, LOCK_DATE_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canSeeDashboardTab = () => {
  return hasReadPermissions(get(getUserInfo(), DASHBOARD_MENU_PERMISSION_KEY, DEFAULT_PERMISSION))
}

export const canSeeCustomersTab = () => {
  return hasReadPermissions(get(getUserInfo(), CUSTOMER_MENU_PERMISSION_KEY, DEFAULT_PERMISSION))
}

export const canSeeRouteTab = () => {
  return hasReadPermissions(get(getUserInfo(), ROUTE_MENU_PERMISSION_KEY, DEFAULT_PERMISSION))
}

export const canSeeBillingTab = () => {
  return hasReadPermissions(get(getUserInfo(), BILLING_MENU_PERMISSION_KEY, DEFAULT_PERMISSION))
}

export const canSeeReportTab = () => {
  return CAN_SEE_REPORTS && hasReadPermissions(get(getUserInfo(), REPORT_MENU_PERMISSION_KEY, DEFAULT_PERMISSION))
}

export const canSeeReportingTab = () => {
  return hasReadPermissions(get(getUserInfo(), REPORT_MENU_PERMISSION_KEY, DEFAULT_PERMISSION))
}

export const canSeePricingTab = () => {
  return hasReadPermissions(get(getUserInfo(), PRICING_MENU_PERMISSION_KEY, DEFAULT_PERMISSION))
}

export const canSeeFollowUpTab = () => {
  // FOLLOW_UP_PERMISSION_KEY
  return false
}

export const canSeeSettingsTab = () => {
  return hasReadPermissions(get(getUserInfo(), SETTING_MENU_PERMISSION_KEY, DEFAULT_PERMISSION))
}

export const canSeeNotifications = () => {
  return hasReadPermissions(get(getUserInfo(), NOTIFICATION_PERMISSION_KEY, DEFAULT_PERMISSION))
}

export const canSeeExceptionsTab = () => {
  return hasReadPermissions(get(getUserInfo(), EXCEPTIONS_MENU_PERMISSION_KEY, DEFAULT_PERMISSION))
}

// GEO ZONES — `geoZoneCrud` is its own CrudType ladder on the user profile, separate from the
// Pricing menu permission that merely reveals the tab.

const geoZonePermission = () => get(getUserInfo(), GEO_ZONE_PERMISSION_KEY, DEFAULT_PERMISSION)

export const canSeeZones = () => hasReadPermissions(geoZonePermission())

export const canCreateZone = () => hasCreatePermissions(geoZonePermission())

export const canUpdateZone = () => hasUpdatePermissions(geoZonePermission())

export const canDeleteZone = () => hasDeletePermissions(geoZonePermission())

// CUSTOMER LISTS — gated like account groups by the service permission; messaging only gates sending and message stats.

const servicePermission = () => get(getUserInfo(), SERVICE_PERMISSION_KEY, DEFAULT_PERMISSION)

const messagingPermission = () => get(getUserInfo(), MESSAGING_PERMISSION_KEY, DEFAULT_PERMISSION)

export const canSeeCustomerLists = () => hasReadPermissions(servicePermission())

export const canCreateCustomerList = () => hasCreatePermissions(servicePermission())

export const canUpdateCustomerList = () => hasUpdatePermissions(servicePermission())

export const canSeeMessageStats = () => hasReadPermissions(messagingPermission())

export const canSendMessages = () => hasCreatePermissions(messagingPermission())

// ADD PERMISSIONS

export const canAddInvoices = createSelector([selectUserInfo], userInfo => {
  return hasAddPermissions(get(userInfo, INVOICES_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canAddPayments = createSelector([selectUserInfo], userInfo => {
  return hasAddPermissions(get(userInfo, PAYMENTS_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canAddLockDate = createSelector([selectUserInfo], userInfo => {
  return hasAddPermissions(get(userInfo, LOCK_DATE_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canAddCustomerManager = createSelector([selectUserInfo], userInfo => {
  return hasAddPermissions(get(userInfo, CUSTOMER_MENU_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canAddService = createSelector([selectUserInfo], userInfo => {
  return hasAddPermissions(get(userInfo, SERVICE_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canAddCustomerBillingProfiles = createSelector([selectUserInfo], userInfo => {
  return hasAddPermissions(get(userInfo, CUSTOMER_BILLING_PROFILE_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canAddPricingManager = createSelector([selectUserInfo], userInfo => {
  return hasAddPermissions(get(userInfo, PRICING_MENU_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canAddSettings = createSelector([selectUserInfo], userInfo => {
  return hasAddPermissions(get(userInfo, SETTING_MENU_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canCreateSettings = createSelector([selectUserInfo], userInfo => {
  return hasCreatePermissions(get(userInfo, SETTING_MENU_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canAddUsers = createSelector([selectUserInfo], userInfo => {
  return hasAddPermissions(get(userInfo, USER_SETTINGS_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canAddPermissions = createSelector([selectUserInfo], userInfo => {
  return hasAddPermissions(get(userInfo, PERMISSIONS_SETTINGS_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canAddReports = createSelector([selectUserInfo], userInfo => {
  return hasAddPermissions(get(userInfo, REPORT_MENU_PERMISSION_KEY, DEFAULT_PERMISSION))
})

// Update PERMISSIONS

export const canUpdateInvoices = createSelector([selectUserInfo], userInfo => {
  return hasUpdatePermissions(get(userInfo, INVOICES_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canUpdateLockDate = createSelector([selectUserInfo], userInfo => {
  return hasUpdatePermissions(get(userInfo, LOCK_DATE_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canUpdatePricingManager = createSelector([selectUserInfo], userInfo => {
  return hasUpdatePermissions(get(userInfo, PRICING_MENU_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canUpdatePostInvoicesPayment = createSelector([selectUserInfo], userInfo => {
  return hasUpdatePermissions(get(userInfo, POST_INVOICES_PAYMENT_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canUpdateCustomerManager = createSelector([selectUserInfo], userInfo => {
  return hasUpdatePermissions(get(userInfo, CUSTOMER_MENU_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canUpdateService = createSelector([selectUserInfo], userInfo => {
  return hasUpdatePermissions(get(userInfo, SERVICE_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canEditInvoicedWorkOrderDate = createSelector([selectUserInfo], userInfo => {
  return hasUpdatePermissions(get(userInfo, INVOICED_WORK_ORDER_DATE_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canViewInspections = createSelector([selectUserInfo], userInfo => {
  return hasReadPermissions(get(userInfo, INSPECTIONS_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canRepairInspections = createSelector([selectUserInfo], userInfo => {
  return hasCreatePermissions(get(userInfo, INSPECTIONS_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canUpdateSettings = createSelector([selectUserInfo], userInfo => {
  return hasUpdatePermissions(get(userInfo, SETTING_MENU_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canDeleteSettings = createSelector([selectUserInfo], userInfo => {
  return hasDeletePermissions(get(userInfo, SETTING_MENU_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canUpdateUsers = createSelector([selectUserInfo], userInfo => {
  return hasUpdatePermissions(get(userInfo, USER_SETTINGS_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canUpdateResetPassword = createSelector([selectUserInfo], userInfo => {
  return hasUpdatePermissions(get(userInfo, RESET_PASSWORD_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canUpdatePermissions = createSelector([selectUserInfo], userInfo => {
  return hasUpdatePermissions(get(userInfo, PERMISSIONS_SETTINGS_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canUpdatePayments = createSelector([selectUserInfo], userInfo => {
  return hasUpdatePermissions(get(userInfo, PAYMENTS_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canUpdateCustomerBillingProfiles = createSelector([selectUserInfo], userInfo => {
  return hasUpdatePermissions(get(userInfo, CUSTOMER_BILLING_PROFILE_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canUpdateConfiguredServicePrice = createSelector([selectUserInfo], userInfo => {
  return hasUpdatePermissions(get(userInfo, CONFIGURED_SERVICE_PRICE_PERMISSION_KEY, DEFAULT_PERMISSION))
})

export const canUpdateRouteManager = createSelector([selectUserInfo], userInfo => {
  return hasUpdatePermissions(get(userInfo, ROUTE_MENU_PERMISSION_KEY, DEFAULT_PERMISSION))
})
